"use client";

import { c as _c } from "react/compiler-runtime";
import { CheckCircleIcon, ChevronDownIcon, CircleIcon, ClockIcon, WrenchIcon, XCircleIcon } from "lucide-react";
import { isValidElement } from "react";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ImageZoom } from "@/components/ui/image-zoom";
import { cn } from "@/lib/utils";
import { CodeBlock } from "./code-block";
import { Image } from "./image";
import { hasImageModelsOutput, hasModelsOutput, hasTextContentOutput, ImageModelsListOutput, ModelsListOutput, TextContentOutput } from "./tool-results";
import type { ToolUIPart } from "ai";
import type { ComponentProps, ReactNode } from "react";
export type ToolProps = ComponentProps<typeof Collapsible>;
export const Tool = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("not-prose mb-4 w-full rounded-md border", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <Collapsible className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export interface ToolHeaderProps {
  title?: string;
  type: ToolUIPart["type"];
  state: ToolUIPart["state"];
  className?: string;
}
const getStatusBadge = (status: ToolUIPart["state"]) => {
  const labels: Record<ToolUIPart["state"], string> = {
    "input-streaming": "Pending",
    "input-available": "Running",
    "approval-requested": "Approval Requested",
    "approval-responded": "Approval Responded",
    "output-available": "Completed",
    "output-error": "Error",
    "output-denied": "Denied"
  };
  const icons: Record<ToolUIPart["state"], ReactNode> = {
    "input-streaming": <CircleIcon className="size-4" />,
    "input-available": <ClockIcon className="size-4 animate-pulse" />,
    "approval-requested": <ClockIcon className="size-4 text-yellow-600" />,
    "approval-responded": <CheckCircleIcon className="size-4 text-blue-600" />,
    "output-available": <CheckCircleIcon className="size-4 text-green-600" />,
    "output-error": <XCircleIcon className="size-4 text-red-600" />,
    "output-denied": <XCircleIcon className="size-4 text-orange-600" />
  };
  return <Badge className="gap-1.5 rounded-full text-xs" variant="secondary">
			{icons[status]}
			{labels[status]}
		</Badge>;
};
export const ToolHeader = t0 => {
  const $ = _c(24);
  let className;
  let props;
  let state;
  let title;
  let type;
  if ($[0] !== t0) {
    ({
      className,
      title,
      type,
      state,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = state;
    $[4] = title;
    $[5] = type;
  } else {
    className = $[1];
    props = $[2];
    state = $[3];
    title = $[4];
    type = $[5];
  }
  let t1;
  if ($[6] !== className) {
    t1 = cn("flex w-full items-center justify-between gap-4 p-3", className);
    $[6] = className;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <WrenchIcon className="size-4 text-muted-foreground" />;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== title || $[10] !== type) {
    t3 = title ?? type.split("-").slice(1).join("-");
    $[9] = title;
    $[10] = type;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== t3) {
    t4 = <span className="font-medium text-sm">{t3}</span>;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== state) {
    t5 = getStatusBadge(state);
    $[14] = state;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t4 || $[17] !== t5) {
    t6 = <div className="flex items-center gap-2">{t2}{t4}{t5}</div>;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <ChevronDownIcon className="size-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== props || $[21] !== t1 || $[22] !== t6) {
    t8 = <CollapsibleTrigger className={t1} {...props}>{t6}{t7}</CollapsibleTrigger>;
    $[20] = props;
    $[21] = t1;
    $[22] = t6;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
};
export type ToolContentProps = ComponentProps<typeof CollapsibleContent>;
export const ToolContent = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2 text-popover-foreground outline-none data-[state=closed]:animate-out data-[state=open]:animate-in", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <CollapsibleContent className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type ToolInputProps = ComponentProps<"div"> & {
  input: ToolUIPart["input"];
};
export const ToolInput = t0 => {
  const $ = _c(15);
  let className;
  let input;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      input,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = input;
    $[3] = props;
  } else {
    className = $[1];
    input = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("space-y-2 overflow-hidden p-4", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <h4 className="font-medium text-muted-foreground text-xs uppercase tracking-wide">Parameters</h4>;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== input) {
    t3 = JSON.stringify(input, null, 2);
    $[7] = input;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t3) {
    t4 = <div className="rounded-md bg-muted/50"><CodeBlock code={t3} language="json" /></div>;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== props || $[12] !== t1 || $[13] !== t4) {
    t5 = <div className={t1} {...props}>{t2}{t4}</div>;
    $[11] = props;
    $[12] = t1;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
};
export type ToolOutputProps = ComponentProps<"div"> & {
  output: ToolUIPart["output"];
  errorText: ToolUIPart["errorText"];
};

/**
 * Check if output contains image data from generate-image tool
 * Images are returned as { base64: string, mediaType: string } objects
 */
function hasImageOutput(output: unknown): output is {
  images: {
    base64: string;
    mediaType: string;
  }[];
  text?: string;
} {
  if (typeof output !== "object" || output === null || !("images" in output) || !Array.isArray((output as {
    images: unknown;
  }).images)) {
    return false;
  }
  const images = (output as {
    images: unknown[];
  }).images;
  if (images.length === 0) {
    return false;
  }
  const first = images[0];
  return typeof first === "object" && first !== null && "base64" in first && "mediaType" in first && typeof (first as {
    base64: unknown;
  }).base64 === "string" && typeof (first as {
    mediaType: unknown;
  }).mediaType === "string";
}
export const ToolOutput = t0 => {
  const $ = _c(45);
  let className;
  let errorText;
  let output;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      output,
      errorText,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = errorText;
    $[3] = output;
    $[4] = props;
  } else {
    className = $[1];
    errorText = $[2];
    output = $[3];
    props = $[4];
  }
  if (!(output || errorText)) {
    return null;
  }
  const t1 = output as ReactNode;
  let t2;
  if ($[5] !== t1) {
    t2 = <div>{t1}</div>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let Output = t2;
  if (hasImageOutput(output)) {
    let t3;
    if ($[7] !== output.text) {
      t3 = output.text && <p className="text-sm text-muted-foreground">{output.text}</p>;
      $[7] = output.text;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    let t4;
    if ($[9] !== output.images) {
      t4 = output.images.map(_temp);
      $[9] = output.images;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    let t5;
    if ($[11] !== t4) {
      t5 = <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{t4}</div>;
      $[11] = t4;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    let t6;
    if ($[13] !== t3 || $[14] !== t5) {
      t6 = <div className="space-y-4 p-2">{t3}{t5}</div>;
      $[13] = t3;
      $[14] = t5;
      $[15] = t6;
    } else {
      t6 = $[15];
    }
    Output = t6;
  } else {
    if (hasModelsOutput(output)) {
      let t3;
      if ($[16] !== output.models) {
        t3 = <ModelsListOutput models={output.models} />;
        $[16] = output.models;
        $[17] = t3;
      } else {
        t3 = $[17];
      }
      Output = t3;
    } else {
      if (hasImageModelsOutput(output)) {
        let t3;
        if ($[18] !== output.imageModels) {
          t3 = <ImageModelsListOutput imageModels={output.imageModels} />;
          $[18] = output.imageModels;
          $[19] = t3;
        } else {
          t3 = $[19];
        }
        Output = t3;
      } else {
        if (hasTextContentOutput(output)) {
          let t3;
          if ($[20] !== output.response) {
            t3 = <TextContentOutput response={output.response} />;
            $[20] = output.response;
            $[21] = t3;
          } else {
            t3 = $[21];
          }
          Output = t3;
        } else {
          if (typeof output === "object" && !isValidElement(output)) {
            let t3;
            if ($[22] !== output) {
              t3 = JSON.stringify(output, null, 2);
              $[22] = output;
              $[23] = t3;
            } else {
              t3 = $[23];
            }
            let t4;
            if ($[24] !== t3) {
              t4 = <CodeBlock code={t3} language="json" />;
              $[24] = t3;
              $[25] = t4;
            } else {
              t4 = $[25];
            }
            Output = t4;
          } else {
            if (typeof output === "string") {
              let t3;
              if ($[26] !== output) {
                t3 = <CodeBlock code={output} language="json" />;
                $[26] = output;
                $[27] = t3;
              } else {
                t3 = $[27];
              }
              Output = t3;
            }
          }
        }
      }
    }
  }
  let t3;
  if ($[28] !== className) {
    t3 = cn("space-y-2 p-4", className);
    $[28] = className;
    $[29] = t3;
  } else {
    t3 = $[29];
  }
  const t4 = errorText ? "Error" : "Result";
  let t5;
  if ($[30] !== t4) {
    t5 = <h4 className="font-medium text-muted-foreground text-xs uppercase tracking-wide">{t4}</h4>;
    $[30] = t4;
    $[31] = t5;
  } else {
    t5 = $[31];
  }
  const t6 = errorText ? "bg-destructive/10 text-destructive" : "bg-muted/50 text-foreground";
  let t7;
  if ($[32] !== t6) {
    t7 = cn("overflow-x-auto rounded-md text-xs [&_table]:w-full", t6);
    $[32] = t6;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  let t8;
  if ($[34] !== errorText) {
    t8 = errorText && <div>{errorText}</div>;
    $[34] = errorText;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  let t9;
  if ($[36] !== Output || $[37] !== t7 || $[38] !== t8) {
    t9 = <div className={t7}>{t8}{Output}</div>;
    $[36] = Output;
    $[37] = t7;
    $[38] = t8;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  let t10;
  if ($[40] !== props || $[41] !== t3 || $[42] !== t5 || $[43] !== t9) {
    t10 = <div className={t3} {...props}>{t5}{t9}</div>;
    $[40] = props;
    $[41] = t3;
    $[42] = t5;
    $[43] = t9;
    $[44] = t10;
  } else {
    t10 = $[44];
  }
  return t10;
};
function _temp(img, index) {
  return <ImageZoom key={index}><Image base64={img.base64} mediaType={img.mediaType} alt={`Generated image ${index + 1}`} className="h-[400px] aspect-auto border rounded-lg object-cover" /></ImageZoom>;
}